/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable simple-import-sort/imports */
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18next from "i18next";
import { WorkerSchedulerState } from "./worker-scheduler-types";
import axios from "../../../utils/api-client";
import { addDays, addSeconds, subDays } from "date-fns";
import { generateDaysOfTheWeek } from "../../../utils/helpers";
import { DialogType, TransitionDialogState } from "../types/transition-types";
import { defaultStatus, statusColors } from "../utils";
import { setCurrentDate } from "../../full-time-shifts/store/full-time-shifts-slice";
import { StoreType } from "../../../utils/types";
import { FULL_TIME_WORKER_FILTERS } from "../../../assets/constants";

// Define RootState interface type
interface RootState {
	user: {
		userData: {
			id: string;
			weekStartDay?: string;
		};
	};
	workerScheduler: WorkerSchedulerState;
	fullTimeShifts: {
		selectedLocation: number | null;
	};
}

interface WorkerResponse {
	workerFTShifts: any[];
	workerFlexShifts: any[];
}

interface WorkerData {
	workerId: string;
	[key: string]: any;
}

const initialState: WorkerSchedulerState = {
	allWorkers: [],
	allWorkersObj: {},
  currentDate: "",
	searchedWorkers: [],
	schedules: [],
	searchTerm: "",
	requestTypes: [],
	filterData: {
		allJobRoles: [],
		allLocations: [],
		allWorkcenters: [],
		allWorkcentersObj: {},
		allSkills: [],
		allManagers: [],
		jobSkillRelations: [],
		allJobsObj: {},
		shiftReasons: {},
		shiftPatternsWithDates: [],
		workerSchedulerLocations: [],
		workerTypes: [],
		workerTypesObj: {},
		workerStatuses: [],
		workerStatusesObj: {},
		schedulingStatuses: [],
		schedulingStatusesObj: {},
    filterRelationships: [],
	},
	ftWorkerShifts: [],
	ftWorkerWorkcenters: [],
	ftWorkerShiftPatterns: [],
	ftWorkerPrimaryJobs: [],
	ftWorkerFlexShifts: [],
	fetchFTWorkerShiftDetails: {
		status: "idle",
		errorMessage: "",
		data: [],
	},
	fetchFTWorkerShifts: {
		status: "idle",
		errorMessage: "",
	},
	fetchFTWorkerFilters: {
		status: "idle",
		errorMessage: "",
	},
	transitionDialog: {
		dialogType: DialogType.NONE,
		isOpen: false,
		data: null,
		loading: false,
		error: null,
	},
	fetchRequestTypes: {
		status: "idle",
		errorMessage: "",
	},
	fetchWorkerCallOffs: {
		status: "idle",
		errorMessage: "",
		data: [],
	},
	updateWorkerAvailabilityStatus: {
		status: "idle",
		errorMessage: "",
	},
};

export const fetchFTWorkerFilters = createAsyncThunk(
	"workerScheduler/fetchFTWorkerFilters",
	async (payload, thunkAPI) => {
		const weekStartDay =
			(
				thunkAPI.getState() as RootState
			).user?.userData?.weekStartDay?.toLowerCase() || "monday";
		const datesOfTheWeek: any = generateDaysOfTheWeek(new Date(), weekStartDay);

		try {
			const startDateTime = new Date(datesOfTheWeek.monday);
			// Adding 24 hours minus a sec to the end date to cover that last day
			const endDateTime = new Date(
				addSeconds(new Date(datesOfTheWeek.sunday), 24 * 60 * 60 - 1)
			);

			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FlexScheduler/GetFiltersForUser`,
				{
					filters: FULL_TIME_WORKER_FILTERS,
					shiftpatternswithdatesOptions: {
						startDateTime: startDateTime,
						endDateTime: endDateTime,
					},
				}
			);

			return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);

const getWeekDates = (baseDate: Date, weekStartDay: string) => {
	const datesOfTheWeek = generateDaysOfTheWeek(baseDate, weekStartDay);
	const startDateTime = new Date(datesOfTheWeek.monday);
	const endDateTime = new Date(
		addSeconds(new Date(datesOfTheWeek.sunday), 24 * 60 * 60 - 1)
	);
	return { startDateTime, endDateTime, datesOfTheWeek };
};

const fetchWorkerShifts = async (
	startDateTime: Date,
	endDateTime: Date,
	selectedLocation: number | null
): Promise<WorkerResponse> => {
	const response = await axios.get(
		`${process.env.REACT_APP_API_END_POINT}/FTWorkerView/GetFTWorkerView`,
		{
			params: {
				startDate: startDateTime,
				endDate: endDateTime,
				locationId: selectedLocation,
			},
		}
	);
	return {
		workerFTShifts: response.data.workerFTShifts || [],
		workerFlexShifts: response.data.workerFlexShifts || [],
	};
};

export const fetchFTWorkerShifts = createAsyncThunk(
	"workerScheduler/fetchFTWorkerShifts",
	async (payload: string, thunkAPI) => {
		try {
			const state = thunkAPI.getState() as StoreType;
			const weekStartDay =
				state.user?.userData?.weekStartDay?.toLowerCase() || "monday";
			const currentDate = state.fullTimeShifts?.currentDate;

			const selectedLocation = state.fullTimeShifts.selectedLocation;

			const { allWorkers, allWorkersObj } = state.workerScheduler;

			let newDate: Date;
			switch (payload) {
				case "TODAY":
					newDate = new Date();
					break;
				case "NEXT_WEEK":
					newDate = addDays(new Date(currentDate), 7);
					break;
				case "PREVIOUS_WEEK":
					newDate = subDays(new Date(currentDate), 7);
					break;
				case "INITIAL":
					newDate = new Date(currentDate || new Date());
					break;
				default:
					newDate = new Date(payload);
			}

			const { startDateTime, endDateTime } = getWeekDates(
				newDate,
				weekStartDay
			);
			thunkAPI.dispatch(
				setCurrentDate(newDate.toISOString())
			);

			if (payload !== "INITIAL") {
				const shifts = await fetchWorkerShifts(
					startDateTime,
					endDateTime,
					selectedLocation
				);
				return {
					...shifts,
					newDate: newDate.toISOString(),
					allWorkers: allWorkers || [],
					allWorkersObj: allWorkersObj || {},
				};
			}

			const [workersResponse, shifts] = await Promise.all([
				axios.get(
					`${process.env.REACT_APP_API_END_POINT}/FTWorkerView/GetBasicWorkerList`,
					{
						params: { locationId: selectedLocation },
					}
				),
				fetchWorkerShifts(startDateTime, endDateTime, selectedLocation),
			]);

			const newWorkersObj = (workersResponse.data as WorkerData[]).reduce(
				(acc: Record<string, WorkerData>, worker: WorkerData) => {
					acc[worker.workerId] = worker;
					return acc;
				},
				{}
			);

			return {
				...shifts,
				newDate: newDate.toISOString(),
				allWorkers: workersResponse.data || [],
				allWorkersObj: newWorkersObj,
			};
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);

export const fetchRequestTypes = createAsyncThunk(
	"workerScheduler/fetchRequestTypes",
	async (payload, thunkAPI) => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/workers/requestTypes`
			);
			if (response.status === 200) {
				return thunkAPI.fulfillWithValue(response.data);
			} else {
				return thunkAPI.fulfillWithValue([]);
			}
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);

export const fetchWorkerCallOffs = createAsyncThunk(
	"workerScheduler/fetchWorkerCallOffs",
	async (payload: string, thunkAPI) => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/FTSchedulingStatus/GetWorkerLeaveTypesAndBalances`,
				{
					params: {
						workerId: payload,
					},
				}
			);
			if (response.status === 200) {
				return thunkAPI.fulfillWithValue(response.data);
			} else {
				return thunkAPI.fulfillWithValue([]);
			}
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);

export const fetchWorkerFTShiftDetails = createAsyncThunk(
	"workerScheduler/fetchWorkerFTShiftDetails",
	async (
		payload: {
			workcenterId: number;
			shiftId: string;
		}[],
		thunkAPI
	) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FTShiftsView/GetFTShiftsDetails`,
				payload
			);
			if (response.status === 200) {
				return thunkAPI.fulfillWithValue(response.data);
			} else {
				return thunkAPI.fulfillWithValue([]);
			}
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);

export const updateWorkerAvailabilityStatus = createAsyncThunk(
	"workerScheduler/updateWorkerAvailabilityStatus",
	async (payload: any, thunkAPI) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FTSchedulingStatus/UpdateWorkerAvailability`,
				payload
			);
			if (response.status === 200) {
				return thunkAPI.fulfillWithValue(payload);
			} else {
				return thunkAPI.fulfillWithValue([]);
			}
		} catch (error: any) {
			return thunkAPI.rejectWithValue(error.response.data.message);
		}
	}
);

const workerSchedulerSlice = createSlice({
	name: "workerScheduler",
	initialState,
	reducers: {
		setSchedules: (state, action: PayloadAction<any[]>) => {
			state.schedules = action.payload;
		},

		clearSchedulerState: (state) => {
			return initialState;
		},

		setSearchTerm: (state, action: PayloadAction<string>) => {
			state.searchedWorkers = state.allWorkers.filter(
				(worker) =>
					`${worker.firstName} ${worker.lastName}`
						.toLowerCase()
						.includes(action.payload.toLowerCase()) ||
					worker.companyEmployeeId
						.toLowerCase()
						.includes(action.payload.toLowerCase()) ||
					state.filterData.workerTypesObj[worker.workerTypeId]?.name
						.toLowerCase()
						.includes(action.payload.toLowerCase()) ||
					state.filterData.workerSubTypesObj[worker.workerSubTypeId]?.name
						.toLowerCase()
						.includes(action.payload.toLowerCase())
			);
			state.searchTerm = action.payload;
		},
		resetWorkerCallOffs: (state) => {
			state.fetchWorkerCallOffs.data = [];
			state.fetchWorkerCallOffs.status = "idle";
			state.fetchWorkerCallOffs.errorMessage = "";
		},
		openTransitionDialog: (
			state,
			action: PayloadAction<{
				dialogType: DialogType;
				data: any;
			}>
		) => {
			state.transitionDialog = {
				dialogType: action.payload.dialogType,
				isOpen: true,
				data: action.payload.data,
				loading: false,
				error: null,
			};
		},
		closeTransitionDialog: (state) => {
			state.transitionDialog = {
				dialogType: DialogType.NONE,
				isOpen: false,
				data: null,
				loading: false,
				error: null,
			};
		},
		setTransitionDialogLoading: (state, action: PayloadAction<boolean>) => {
			state.transitionDialog.loading = action.payload;
		},
		setTransitionDialogError: (state, action: PayloadAction<string | null>) => {
			state.transitionDialog.error = action.payload;
		},
		resetUpdateWorkerAvailabilityStatus: (state) => {
			state.updateWorkerAvailabilityStatus = {
				status: "idle",
				errorMessage: "",
			};
		},
		resetFetchFTWorkerShiftDetailsStatus: (state) => {
			state.fetchFTWorkerShiftDetails.status = "idle";
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchFTWorkerFilters.pending, (state) => {
			state.fetchFTWorkerFilters.status = "pending";
		});
		builder.addCase(fetchFTWorkerFilters.fulfilled, (state, action) => {
			state.fetchFTWorkerFilters.status = "fulfilled";

			const {
				jobs,
				locations,
				workCenters,
				skills,
				shiftReasons,
				shiftPatternsWithDates,
				shiftPatternWorkCenters,
				workerTypes,
				managers,
				workerSubTypes,
				workerStatuses,
				schedulingStatuses,
        filterRelationships,
			} = action.payload;

			const shiftPatternWorkCenterIds = shiftPatternWorkCenters.map(
				(shiftPattern: { patternId: number; workCenterIds: string }) => ({
					shiftPatternId: shiftPattern.patternId,
					workCenterIds: shiftPattern.workCenterIds.split(",").map(Number),
				})
			);
			// console.log("Shift Pattern Work Center Ids", shiftPatternWorkCenterIds);
			const workcentersWithShiftPatternAndJob = workCenters
				.map((workCenter: { id: number; jobIds: string }) => {
					return {
						...workCenter,
						jobs: workCenter?.jobIds?.split(",").map(Number) ?? [],
						shiftPatterns:
							shiftPatternWorkCenterIds
								.filter((shiftPattern: { workCenterIds: number[] }) =>
									shiftPattern.workCenterIds.includes(workCenter.id)
								)
								.map(
									(shiftPattern: { shiftPatternId: number }) =>
										shiftPattern.shiftPatternId
								) ?? [],
					};
				})
				.sort((a: any, b: any) => a.name.localeCompare(b.name));

			const shiftPatternsObj = shiftPatternsWithDates.reduce(
				(acc: any, shiftPattern: any) => {
					acc[shiftPattern.id] = shiftPattern;
					return acc;
				},
				{}
			);

			const workcentersObj = workCenters.reduce((acc: any, workcenter: any) => {
				acc[workcenter.id] = workcenter;
				return acc;
			}, {});

			const jobsObj = jobs.reduce((acc: any, job: any) => {
				acc[job.id] = job;
				return acc;
			}, {});

			const workerTypesObj = workerTypes.reduce((acc: any, workerType: any) => {
				acc[workerType.id] = workerType;
				return acc;
			}, {});

			const workerSubTypesObj = workerSubTypes.reduce(
				(acc: any, workerSubType: any) => {
					acc[workerSubType.id] = workerSubType;
					return acc;
				},
				{}
			);

			const workerStatusesObj = workerStatuses.reduce(
				(acc: any, workerStatus: any) => {
					acc[workerStatus.id] = workerStatus;
					return acc;
				},
				{}
			);

			const schedulingStatusesObj = schedulingStatuses.reduce(
				(acc: any, schedulingStatus: any) => {
					const code = schedulingStatus.code;
					const color = statusColors[code as keyof typeof statusColors];
					acc[schedulingStatus.code] = {
						...schedulingStatus,
						...color,
					};
					return acc;
				},
				{}
			);

			state.filterData = {
				allJobs: jobs ?? [],
				allLocations: locations ?? [],
				allWorkcenters: workcentersWithShiftPatternAndJob ?? [],
				allWorkcentersObj: workcentersObj,
				allSkills: skills ?? [],
				allManagers: managers ?? [],
				shiftReasons: shiftReasons ?? {},
				shiftPatternsWithDates: shiftPatternsWithDates ?? [],
				shiftPatternsObj: shiftPatternsObj ?? {},
				allJobsObj: jobsObj ?? {},
				workerTypes: workerTypes ?? [],
				workerTypesObj: workerTypesObj ?? {},
				workerSubTypes: workerSubTypes ?? [],
				workerSubTypesObj: workerSubTypesObj ?? {},
				workerStatuses: workerStatuses
					.filter(
						(workerStatus: { code: string }) => workerStatus.code !== "INA"
					)
					.map((workerStatus: { workerStatusId: number; name: string }) => ({
						id: workerStatus.workerStatusId,
						name: workerStatus.name,
					})),
				workerStatusesObj: workerStatusesObj ?? {},
				schedulingStatuses: schedulingStatuses ?? [],
				schedulingStatusesObj: schedulingStatusesObj ?? {},
        filterRelationships: filterRelationships ?? [],
			};
		});
		builder.addCase(fetchFTWorkerFilters.rejected, (state, action) => {
			state.fetchFTWorkerFilters.status = "error";
			state.fetchFTWorkerFilters.errorMessage =
				action.error.message ||
				i18next.t("full-time-worker-scheduler_error-fetching-filters");
		});
		builder.addCase(fetchFTWorkerShifts.pending, (state) => {
			state.fetchFTWorkerShifts.status = "pending";
		});
		builder.addCase(fetchFTWorkerShifts.fulfilled, (state, action) => {
			state.fetchFTWorkerShifts.status = "fulfilled";

			const {
				workerFTShifts,
				workerFlexShifts,
				newDate,
				allWorkers,
				allWorkersObj,
			} = action.payload;

			state.ftWorkerShifts = workerFTShifts;
			state.ftWorkerFlexShifts = workerFlexShifts;
			state.ftWorkerWorkcenters = state.ftWorkerShifts.map(
				(shift: any) => shift.dwCenterId
			);
			state.ftWorkerShiftPatterns = state.ftWorkerShifts.map(
				(shift: any) => shift.dShiftPatternId
			);
			state.ftWorkerPrimaryJobs = state.ftWorkerShifts.map(
				(shift: any) => shift.dPrimaryJobId
			);
			state.allWorkers = allWorkers;
			state.allWorkersObj = allWorkersObj;
			state.currentDate = newDate;

			//Re-apply the search filter if it exists
			if (state.searchTerm) {
				state.searchedWorkers = allWorkers.filter(
					(worker: any) =>
						`${worker.firstName} ${worker.lastName}`
							.toLowerCase()
							.includes(state.searchTerm.toLowerCase()) ||
						worker.companyEmployeeId
							.toLowerCase()
							.includes(state.searchTerm.toLowerCase()) ||
						state.filterData.workerTypesObj[worker.workerTypeId]?.name
							.toLowerCase()
							.includes(state.searchTerm.toLowerCase()) ||
						state.filterData.workerSubTypesObj[worker.workerSubTypeId]?.name
							.toLowerCase()
							.includes(state.searchTerm.toLowerCase())
				);
			} else {
				state.searchedWorkers = allWorkers;
			}
		});
		builder.addCase(fetchFTWorkerShifts.rejected, (state, action) => {
			state.fetchFTWorkerShifts.status = "error";
			state.fetchFTWorkerShifts.errorMessage =
				action.error.message ||
				i18next.t("full-time-worker-scheduler_error-fetching-shifts");
		});
		builder.addCase(fetchRequestTypes.pending, (state) => {
			state.fetchRequestTypes.status = "pending";
		});
		builder.addCase(fetchRequestTypes.fulfilled, (state, action) => {
			state.fetchRequestTypes.status = "fulfilled";
			state.requestTypes = action.payload.filter(
				(requestType: { code: string }) =>
					requestType.code.toLowerCase() === "sc" ||
					requestType.code.toLowerCase() === "le" ||
					requestType.code.toLowerCase() === "al"
			);
		});
		builder.addCase(fetchRequestTypes.rejected, (state, action) => {
			state.fetchRequestTypes.status = "error";
			state.fetchRequestTypes.errorMessage =
				action.error.message ||
				i18next.t("full-time-worker-scheduler_error-fetching-request-types");
		});
		builder.addCase(fetchWorkerCallOffs.pending, (state) => {
			state.fetchWorkerCallOffs.status = "pending";
		});
		builder.addCase(fetchWorkerCallOffs.fulfilled, (state, action) => {
			state.fetchWorkerCallOffs.status = "fulfilled";
			state.fetchWorkerCallOffs.data = action.payload;
		});
		builder.addCase(fetchWorkerCallOffs.rejected, (state, action) => {
			state.fetchWorkerCallOffs.status = "error";
			state.fetchWorkerCallOffs.errorMessage =
				action.error.message ||
				i18next.t("full-time-worker-scheduler_error-fetching-time-offs");
		});
		builder.addCase(fetchWorkerFTShiftDetails.pending, (state) => {
			state.fetchFTWorkerShiftDetails.status = "pending";
		});
		builder.addCase(fetchWorkerFTShiftDetails.fulfilled, (state, action) => {
			state.fetchFTWorkerShiftDetails.status = "fulfilled";
			state.fetchFTWorkerShiftDetails.data = action.payload;
		});
		builder.addCase(fetchWorkerFTShiftDetails.rejected, (state, action) => {
			state.fetchFTWorkerShiftDetails.status = "error";
			state.fetchFTWorkerShiftDetails.errorMessage =
				action.error.message ||
				i18next.t("full-time-worker-scheduler_error-fetching-shift-details");
		});
		builder.addCase(updateWorkerAvailabilityStatus.pending, (state) => {
			state.updateWorkerAvailabilityStatus.status = "pending";
		});
		builder.addCase(
			updateWorkerAvailabilityStatus.fulfilled,
			(state, action) => {
				state.updateWorkerAvailabilityStatus.status = "fulfilled";
				state.ftWorkerShifts = state.ftWorkerShifts.map((shift) => {
					if (
						shift.workerId === action.payload.workerId &&
						shift.shiftId === action.payload.ftShiftId
					) {
						const updatedShift = {
							...shift,
							schedulingStatusId:
								action.payload.toStatusCode === "RCOF"
									? state.filterData.schedulingStatusesObj[
											action.payload.lastStatus
									  ]?.id
									: state.filterData.schedulingStatusesObj[
											action.payload.toStatusCode
									  ]?.id,
							previousSchedulingStatusId:
								state.filterData.schedulingStatusesObj[
									action.payload.fromStatusCode
								]?.id,
						};

 					if(action.payload.toStatusCode === "COF" && action.payload.callOffParams?.leaveTypes) {
 						const workerCallOffsData = state.fetchWorkerCallOffs.data || [];
 						updatedShift.leaveTypes = action.payload.callOffParams.leaveTypes.map(
 							(lt: { leaveTypeId: number; requestedHours: number }) => {
 								const found = workerCallOffsData.find((w: any) => w.id === lt.leaveTypeId);
 								return {
 									leaveTypeId: lt.leaveTypeId,
 									requestedHours: lt.requestedHours,
 									name: found?.name || "",
 								};
 							}
 						);
 						updatedShift.requestTypeCode = state.requestTypes.find(
 							(rt: any) => rt.id === action.payload.callOffParams.requestTypeId
 						)?.code || "";
 					}

 					if(action.payload.toStatusCode === "RAS") {
 						updatedShift.uPrimaryJobId = action.payload.reAssignParams.jobId;
 						updatedShift.uwCenterId = action.payload.reAssignParams.workcenterId;
 					}

 					if(action.payload.toStatusCode === "SCH") {
							updatedShift.uPrimaryJobId = state.allWorkers.find(
								(worker) => worker.workerId === action.payload.workerId
							)?.primaryJobId || null;
							updatedShift.uwCenterId = state.allWorkers.find(
								(worker) => worker.workerId === action.payload.workerId
							)?.workCenterId || null;
						}

						return updatedShift;
					}
					return shift;
				});
			}
		);
		builder.addCase(
			updateWorkerAvailabilityStatus.rejected,
			(state, action) => {
				state.updateWorkerAvailabilityStatus.status = "error";
				state.updateWorkerAvailabilityStatus.errorMessage =
					(action.payload as string) ||
					i18next.t("full-time-worker-scheduler_error-updating-availability");
			}
		);
	},
});

export const {
  setSchedules,
  clearSchedulerState,
  setSearchTerm,
  openTransitionDialog,
  closeTransitionDialog,
  setTransitionDialogLoading,
  setTransitionDialogError,
  resetUpdateWorkerAvailabilityStatus,
  resetWorkerCallOffs,
  resetFetchFTWorkerShiftDetailsStatus,
} = workerSchedulerSlice.actions;

export default workerSchedulerSlice.reducer;
